import React, { useEffect, useMemo, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "./App.css";
import { SidebarContext } from "./contexts/SidebarContext";
import { VeilleProvider } from "./contexts/VeilleContext";

// ─── Pages ────────────────────────────────────────────────────────────────────
import DashboardOverviewPage from "./pages/Dashboard/DashboardOverviewPage";
import Dashboard from "./pages/Dashboard/Dashboard";
import ESGIndicatorsPage from "./pages/ESGIndicators/ESGIndicatorsPage";
import OrganisationPage from "./pages/Organisation/OrganisationPage";
import Login from "./pages/Login/Login";
import CarbonFootprintPage from "./pages/CarbonFootprint/CarbonFootprintPage";
import CanevasPage from "./pages/DataEntry/CanevasPage";
import SaisiePage from "./pages/DataEntry/SaisiePage";
import AlignmentModule from "./pages/Alignment/AlignmentModule";
import AlignmentModuleClassic from "./pages/Alignment/AlignmentModuleClassic";
import ContextPage from "./pages/Alignment/context/ContextPage";
import DashboardPage from "./pages/Alignment/dashboard/DashboardPage";
import EvaluationPage from "./pages/Alignment/evaluation/EvaluationPage";
import WorkInProgress from "./pages/Alignment/WorkInProgress";
import ReferentielPage from "./pages/Alignment/referentiel/ReferentielPage";
import EnergyPage from "./pages/DataAnalysis/EnergyPage";
import WaterPage from "./pages/DataAnalysis/WaterPage";
import ReportsPage from "./pages/Reports/ReportsPage";
import ComingSoonPage from "./pages/ComingSoon/ComingSoonPage";
import VeilleRSEPage from "./pages/VeilleRSE/VeilleRSEPage";
import YameeraAnalystPage from "./pages/DataAnalysis/YameeraAnalystPage";
import { ClipboardList, Compass, Sprout, Wind } from "lucide-react";

// ─── Shared Components ────────────────────────────────────────────────────────
import ProtectedRoute from "./Components/ProtectedRoute/ProtectedRoute";
import Sidebar from "./Components/Sidebar/Sidebar";
import AlignmentSidebar from "./pages/Alignment/AlignmentSidebar";

// ─── Hooks ────────────────────────────────────────────────────────────────────
import { useAuthToken } from "./hooks/user/useAuthToken";
import { useGetCurrentUser } from "./hooks";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "./constants";

import type { User as BackendUser } from "./types/user";

export type UserRole = "agent" | "user" | "superuser" | "admin";

// Replace the local User shape with an alias to the backend type
export type User = BackendUser;

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [alignmentNorm, setAlignmentNorm] = useState("GRI");
  const loginMutation = useAuthToken();
  const { refetch } = useGetCurrentUser({ enabled: false });

  // Track viewport size for responsive layout
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // on startup, force user to login (no auto-login)
  const { data: currentUser } = useGetCurrentUser({ enabled: false }); // Fetch current user data

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log("[App] startup - clearing any existing tokens to force login");
        // Always clear tokens on startup to force fresh login
        [
          "authToken",
          "ACCESS_TOKEN",
          "access_token",
          "auth_token",
          "refreshToken",
          "REFRESH_TOKEN",
          "refresh_token",
        ].forEach((k) => localStorage.removeItem(k));

        // Clear session storage flags
        sessionStorage.removeItem("loggedOut");

        // Always start with no user to force login
        setUser(null);
        (window as any).__CURRENT_USER__ = null;
      } catch (error) {
        console.warn("[App] startup clear failed:", error);
        setUser(null);
      } finally {
        setInitializing(false);
      }
    };

    initializeAuth();
  }, []); // run once on mount

  // when current-user query returns, store it in App state
  useEffect(() => {
    if (currentUser) {
      setUser(currentUser);
    }
  }, [currentUser]);

  const handleLogin = async (username: string, password: string): Promise<User> => {
    try {
      console.log("[App] Starting login process for:", username);

      // Clear any existing tokens first
      [
        "authToken",
        "ACCESS_TOKEN",
        "access_token",
        "auth_token",
        "refreshToken",
        "REFRESH_TOKEN",
        "refresh_token",
      ].forEach((k) => localStorage.removeItem(k));

      // perform login (gets tokens)
      const resp = await loginMutation.mutateAsync({ username, password });

      console.log("[App] Login response received:", {
        hasAccess: !!resp.access,
        hasRefresh: !!resp.refresh,
      });

      // store tokens under common keys so fetchClient can pick them up
      const access = resp.access ?? resp.token ?? resp.access_token;
      const refresh = resp.refresh ?? resp.refresh_token;

      if (access) {
        localStorage.setItem("authToken", access);
        localStorage.setItem(ACCESS_TOKEN ?? "ACCESS_TOKEN", access);
        console.log("[App] Access token stored");
      }

      if (refresh) {
        localStorage.setItem("refreshToken", refresh);
        localStorage.setItem(REFRESH_TOKEN ?? "REFRESH_TOKEN", refresh);
        console.log("[App] Refresh token stored");
      }

      // Always try to fetch user data after successful login
      console.log("[App] Fetching user data after login");
      const userRes = await refetch();
      if (userRes.data) {
        console.log("[App] User data fetched successfully:", {
          id: userRes.data.id,
          username: userRes.data.username,
          role: userRes.data.role,
          sites: userRes.data.sites,
        });
        setUser(userRes.data);
        (window as any).__CURRENT_USER__ = userRes.data;
        return userRes.data; // Return user data for redirect
      } else {
        console.error("[App] Failed to fetch user data after login");
        throw new Error("Failed to fetch user data after login");
      }
    } catch (err: any) {
      console.error("Login failed in App.handleLogin:", err);
      // Clear tokens on login failure
      [
        "authToken",
        "ACCESS_TOKEN",
        "access_token",
        "auth_token",
        "refreshToken",
        "REFRESH_TOKEN",
        "refresh_token",
      ].forEach((k) => localStorage.removeItem(k));
      setUser(null);
      throw err;
    }
  };

  const handleLogout = () => {
    [
      "authToken", "ACCESS_TOKEN", "access_token", "auth_token",
      "refreshToken", "REFRESH_TOKEN", "refresh_token",
    ].forEach((k) => localStorage.removeItem(k));
    setUser(null);
  };

  // render Sidebar only when route is not /login
  // Default to closed on mobile so content is visible
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);

  const LocationAwareSidebar = () => {
    const location = useLocation();
    if (!user || location.pathname.startsWith("/login")) return null;

    if (location.pathname.startsWith("/alignment")) {
      return (
        <AlignmentSidebar
          collapsed={!sidebarOpen}
          onToggle={() => setSidebarOpen((o) => !o)}
          selectedNorm={alignmentNorm}
          onSelectNorm={setAlignmentNorm}
          user={user!}
          onLogout={handleLogout}
        />
      );
    }

    return (
      <Sidebar user={user!} isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} onLogout={handleLogout} />
    );
  };

  const MainContentWrapper = () => {
    return (
      <div
        className="app-main-content"
        style={
          isMobile
            ? { marginLeft: 0, width: "125vw" }
            : {
              marginLeft: sidebarOpen ? "256px" : "80px",
              width: sidebarOpen ? "calc(125vw - 256px)" : "calc(125vw - 80px)",
            }
        }
      >
        <Routes>
              <Route path="/login" element={<Login onLogin={handleLogin} user={user} />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute user={user}>
                    <DashboardOverviewPage user={user!} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/organisation"
                element={<Navigate to="/organisation/gestion-interne" replace />}
              />
              <Route
                path="/organisation/gestion-interne"
                element={
                  <ProtectedRoute user={user} requiredRole="admin">
                    <OrganisationPage user={user!} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/organisation/suivi-saisies"
                element={
                  <ProtectedRoute user={user} requiredRole="admin">
                    <Dashboard user={user!} />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/data-entry/canevas"
                element={
                  <ProtectedRoute user={user} requiredRole={["user", "superuser", "admin"]}>
                    <CanevasPage user={user!} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/data-entry/validation"
                element={
                  <ProtectedRoute user={user} requiredRole={["agent", "admin"]}>
                    <SaisiePage user={user!} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/carbon"
                element={
                  <ProtectedRoute user={user}>
                    <CarbonFootprintPage user={user!} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/carbon/decarbonation"
                element={
                  <ProtectedRoute user={user}>
                    <ComingSoonPage
                      user={user!}
                      title="Décarbonation"
                      description="Définissez et pilotez votre stratégie de décarbonation : fixez des objectifs de réduction, suivez vos actions et mesurez vos progrès vers la neutralité carbone."
                      icon={<Wind size={30} />}
                      accentColor="#0891b2"
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/esg"
                element={
                  <ProtectedRoute user={user}>
                    <ESGIndicatorsPage />
                  </ProtectedRoute>
                }
              />
              {/* Topbar "Alignement RSE" → classic layout with AlignmentSidebar */}
              <Route
                path="/alignment"
                element={
                  <ProtectedRoute user={user}>
                    <AlignmentModuleClassic
                      user={user!}
                      selectedNorm={alignmentNorm}
                      setSelectedNorm={setAlignmentNorm}
                    />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="context" element={<ContextPage />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="evaluation" element={<EvaluationPage />} />
                <Route path="dossier" element={<WorkInProgress normLabel="Dossier d'audit" />} />
                <Route path="referentiel" element={<ReferentielPage />} />
                <Route path="objectif" element={<WorkInProgress normLabel="Objectif de la solution" />} />
              </Route>
              {/* Sidebar "Alignement référentiel" → new layout with horizontal tabs */}
              <Route
                path="/alignement-referentiel"
                element={
                  <ProtectedRoute user={user}>
                    <AlignmentModule user={user!} />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="context" element={<ContextPage />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="evaluation" element={<EvaluationPage />} />
                <Route path="dossier" element={<WorkInProgress normLabel="Dossier d'audit" />} />
                <Route path="referentiel" element={<ReferentielPage />} />
                <Route path="objectif" element={<WorkInProgress normLabel="Objectif de la solution" />} />
              </Route>
              <Route
                path="/data-analysis/energie"
                element={
                  <ProtectedRoute user={user}>
                    <EnergyPage user={user!} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/data-analysis/eau"
                element={
                  <ProtectedRoute user={user}>
                    <WaterPage user={user!} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/data-analysis/yameera-analyst"
                element={
                  <ProtectedRoute user={user}>
                    <YameeraAnalystPage user={user!} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rapport-indicateurs"
                element={
                  <ProtectedRoute user={user}>
                    <ReportsPage user={user!} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/diagnostic-rse"
                element={
                  <ProtectedRoute user={user}>
                    <ComingSoonPage
                      user={user!}
                      title="Diagnostic RSE"
                      description="Cette section vous permettra d'évaluer la maturité RSE de votre organisation à travers des questionnaires, des analyses et des tableaux de bord dédiés."
                      icon={<ClipboardList size={30} />}
                      accentColor="#e11d48"
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/strategie-rse"
                element={
                  <ProtectedRoute user={user}>
                    <ComingSoonPage
                      user={user!}
                      title="Stratégie RSE"
                      description="Définissez et pilotez votre feuille de route RSE, fixez vos objectifs de développement durable et suivez l'avancement de vos engagements stratégiques."
                      icon={<Compass size={30} />}
                      accentColor="#0037dc"
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rapport-durabilite"
                element={
                  <ProtectedRoute user={user}>
                    <ComingSoonPage
                      user={user!}
                      title="Rapport de durabilité"
                      description="Générez des rapports de durabilité conformes aux référentiels internationaux (GRI, CSRD, TCFD) et communiquez vos performances extra-financières."
                      icon={<Sprout size={30} />}
                      accentColor="#15803d"
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/veille-rse"
                element={
                  <ProtectedRoute user={user}>
                    <VeilleRSEPage user={user!} />
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </div>
        );
  };

  const sidebarContextValue = useMemo(
    () => ({ toggleSidebar: () => setSidebarOpen((prev) => !prev) }),
    []
  );

  if (initializing) {
    return null;
  }

  return (
    <Router basename="/">
      <VeilleProvider>
        <SidebarContext.Provider value={sidebarContextValue}>
          <div className="App" style={{ display: "flex", minHeight: "125vh" }}>
            <LocationAwareSidebar />
            <MainContentWrapper />
          </div>
        </SidebarContext.Provider>
      </VeilleProvider>
    </Router>
  );
}

export default App;
